import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {DragDirection, Span} from '@Libraries/timeline/types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {OverlappingSubtitleItemResizeData} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';

export const isOverlapping = (updatedRange: Span, startTime: number, endTime: number): boolean => {
  return updatedRange.start < endTime && updatedRange.end > startTime;
};

export const areSubtitlesOverlappingWithThisRange = (updatedRange: Span, idOfItemBeingEdited: string, transcriptItem: TranscriptItem): boolean => {
  const pageStartTime = transcriptItem.getPageStartTime();
  for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
    if (key !== idOfItemBeingEdited && isOverlapping(updatedRange, item.startTime + pageStartTime, item.endTime + pageStartTime)) {
      return true;
    }
  }
  return false;
};

export const getOverlapAndSubtitleIds = (
  updatedRange: Span,
  idOfItemBeingEdited: string,
  direction: DragDirection,
  transcriptObject: TranscriptItemObject
): OverlappingSubtitleItemResizeData | null => {
  const transcriptItem = window.posterEditor?.whiteboard?.getItemForId(transcriptObject.uid) as TranscriptItem;
  const pageStartTime = transcriptItem.getPageStartTime();

  if (!transcriptItem) {
    return null;
  }

  const sortedIds: [string, number, number][] = [];
  for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
    sortedIds.push([key, item.startTime + pageStartTime, item.endTime + pageStartTime]);
  }

  if (direction === 'end') {
    sortedIds.sort((a, b) => {
      return a[1] - b[1];
    });
    const startIndex = sortedIds.findIndex((row) => {
      return row[0] === idOfItemBeingEdited;
    });
    if (startIndex === sortedIds.length - 1) {
      return null;
    }

    const overlap = updatedRange.end - sortedIds[startIndex + 1][1];
    if (overlap < 0) {
      return null;
    }

    return {
      overlap,
      ids: sortedIds.slice(startIndex + 1).map((subArray) => {
        return subArray[0];
      }),
      direction,
    };
  }
  sortedIds.sort((a, b) => {
    return a[2] - b[2];
  });
  const startIndex = sortedIds.findIndex((row) => {
    return row[0] === idOfItemBeingEdited;
  });
  if (startIndex === 0) {
    return null;
  }
  const overlap = sortedIds[startIndex - 1][2] - updatedRange.start;
  if (sortedIds[0][1] - overlap < pageStartTime) {
    return null;
  }

  if (overlap < 0) {
    return null;
  }

  return {
    overlap: overlap * -1,
    ids: sortedIds.slice(0, startIndex).map((subArray) => {
      return subArray[0];
    }),
    direction,
  };
};

export const canItemBeMoveToThisRange = (range: Span, direction: DragDirection, idOfItemBeingEdited: string, transcriptItem: TranscriptItem): boolean => {
  const pageEndTime = transcriptItem.page.getEndTime();
  const pageStartTime = transcriptItem.page.getStartTime();
  if (!transcriptItem) {
    throw new Error('unable to find the transcript item');
  }

  if (range.start < pageStartTime) {
    return false;
  }

  const actualDuration = transcriptItem.subtitlesHashmap[idOfItemBeingEdited].endTime - transcriptItem.subtitlesHashmap[idOfItemBeingEdited].startTime;
  const resizeDuration = range.end - range.start;
  const difference = resizeDuration - actualDuration;

  if (difference < 0) {
    return true;
  }

  if (!areSubtitlesOverlappingWithThisRange(range, idOfItemBeingEdited, transcriptItem)) {
    return true;
  }

  const sortedIds: [string, number, number][] = [];
  for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
    sortedIds.push([key, item.startTime + pageStartTime, item.endTime + pageStartTime]);
  }

  if (direction === 'end') {
    sortedIds.sort((a, b) => {
      return a[1] - b[1];
    });
    const startIndex = sortedIds.findIndex((row) => {
      return row[0] === idOfItemBeingEdited;
    });
    if (startIndex === sortedIds.length - 1) {
      return true;
    }

    const overlap = range.end - sortedIds[startIndex + 1][1];
    if (sortedIds[sortedIds.length - 1][2] + overlap > pageEndTime) {
      return false;
    }
  } else {
    sortedIds.sort((a, b) => {
      return a[2] - b[2];
    });
    const startIndex = sortedIds.findIndex((row) => {
      return row[0] === idOfItemBeingEdited;
    });
    const overlap = sortedIds[startIndex - 1][2] - range.start;
    if (sortedIds[0][1] - overlap < pageStartTime) {
      return false;
    }
  }

  return true;
};
